import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Box, Button, Grid, Typography } from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { SupplierDirectorySort } from 'models/supplier-request';
import { DirectoryBrowserConfig, DirectoryLab, DirectoryPage } from './directory-browser-config';
import SupplierCard from './supplier-card.component';
import SupplierDetailsDialog from './supplier-details-dialog.component';
import SupplierBrowserFilters, { SupplierBrowserFilterState } from './supplier-browser-filters.component';
import SupplierMapDialog from './supplier-map-dialog.component';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

const INITIAL_FILTERS: SupplierBrowserFilterState = {
    searchText: '',
    services: [],
    // Nearest first; the server sorts by name while the browsing organization has no location.
    sort: SupplierDirectorySort.DISTANCE,
    maxDistanceKm: 0,
    // DD-3674: labs already connected are hidden until asked to see them.
    hideConnected: true,
    country: '',
};

type DirectoryBrowserProps = {
    config: DirectoryBrowserConfig;
};

/**
 * DD-3662: labs listed in a directory. Search, filter on services and distance, read a
 * profile and act on a lab (request, connect).
 * DD-3674: "Show map" opens the same labs as pins in a large dialog.
 * DD-3696: shared by Find suppliers and Find lab; `config` says which.
 */
const DirectoryBrowser: FC<DirectoryBrowserProps> = ({ config }) => {
    const { t: translate } = useTranslation(['client-directory']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [filters, setFilters] = useState<SupplierBrowserFilterState>(INITIAL_FILTERS);
    const [search, setSearch] = useState('');
    const [readMore, setReadMore] = useState<DirectoryLab>();
    const [isMapOpen, setIsMapOpen] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => setSearch(filters.searchText.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [filters.searchText]);

    const showsHideConnected = config.hideConnectedFilter !== false;
    // Shared by the list and the map dialog, so both show the same labs.
    const filterVariables = {
        search: search || undefined,
        services: filters.services.length ? filters.services : undefined,
        sort: filters.sort,
        maxDistanceKm: filters.maxDistanceKm || undefined,
        hideConnected: (showsHideConnected && filters.hideConnected) || undefined,
        country: (config.countryFilter && filters.country) || undefined,
    };
    const {
        data,
        previousData,
        loading: isLoading,
        fetchMore,
    } = useQuery<Record<string, DirectoryPage>>(config.query, {
        variables: { ...filterVariables, limit: PAGE_SIZE, offset: 0 },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        onError: (error) => showBoundary(error),
    });
    const page = data?.[config.field];
    const labs = page?.labs ?? [];
    // Keep the distance controls steady while a new filter combination loads.
    const canUseDistance = (page ?? previousData?.[config.field])?.ownLocationKnown ?? false;
    const isFiltered = Boolean(
        search ||
            filters.services.length ||
            (showsHideConnected && filters.hideConnected) ||
            (canUseDistance && filters.maxDistanceKm),
    );
    // The open profile follows the list, so its action shows a change made from it.
    const readMoreLab = readMore ? labs.find((lab) => lab._id === readMore._id) ?? readMore : undefined;

    const renderResults = () => {
        if (!page) return <Loader />;
        if (!labs.length) {
            return (
                <Typography variant="body2" color="text.secondary" data-test="supplier-browser.empty">
                    {translate(isFiltered ? config.texts.noneMatch : config.texts.noneListed)}
                </Typography>
            );
        }
        return (
            <Fragment>
                <Grid container spacing={2}>
                    {labs.map((lab) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={lab._id}>
                            <SupplierCard lab={lab} config={config} onReadMore={setReadMore} />
                        </Grid>
                    ))}
                </Grid>
                {labs.length < page.total ? (
                    <Box display="flex" justifyContent="center" mt={3}>
                        <Button
                            disabled={isLoading}
                            onClick={() => fetchMore({ variables: { offset: labs.length } })}
                            data-test="supplier-browser.load-more"
                        >
                            {translate('client-directory:LOAD_MORE_SUPPLIERS')}
                        </Button>
                    </Box>
                ) : undefined}
            </Fragment>
        );
    };

    return (
        <Fragment>
            {/* Boxed like the order list's filters. */}
            <Box
                sx={{
                    marginTop: 1,
                    bgcolor: 'background.paper',
                    padding: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
                data-test="supplier-browser.filters"
            >
                {/* The map button at the right end of the search row. */}
                <SupplierBrowserFilters
                    filters={filters}
                    onChange={(changes) => setFilters((current) => ({ ...current, ...changes }))}
                    canUseDistance={canUseDistance}
                    config={config}
                    actions={
                        <Fragment>
                            <Button
                                startIcon={<MapOutlinedIcon />}
                                onClick={() => setIsMapOpen(true)}
                                data-test="supplier-browser.show-map"
                            >
                                {translate('client-directory:SHOW_SUPPLIER_MAP')}
                            </Button>
                        </Fragment>
                    }
                />
            </Box>
            <Box mt={2} />
            {config.renderNotice?.()}
            {renderResults()}
            {/* Before the profile dialog, so a pin's profile opens on top of the map. */}
            <SupplierMapDialog
                open={isMapOpen}
                onClose={() => setIsMapOpen(false)}
                filterVariables={filterVariables}
                onSelect={setReadMore}
                config={config}
            />
            <SupplierDetailsDialog lab={readMoreLab} onClose={() => setReadMore(undefined)} config={config} />
        </Fragment>
    );
};

export default DirectoryBrowser;
